/**
 * Client for the in-app product assistant ("Herzens Assistent").
 *
 * The contract is fixed by the backend:
 *   GET  /help-assistant/bootstrap?lang=&pageKey=
 *   POST /help-assistant/chat
 *   POST /help-assistant/feedback
 *   POST /help-assistant/event
 *
 * The endpoints may not exist yet in every environment, so every call here is
 * written to fail softly: the analytics/feedback calls swallow their errors
 * completely and the bootstrap query never retries (a 404 must not turn into
 * four 404s per page).
 */
import { apiClient } from '../../../utils/api';

export interface HelpRoute {
  /** Stable key the backend uses in actions, e.g. `clients`. */
  key: string;
  /** In-app path, e.g. `/patients`. */
  path: string;
  /** Human label in the requested language. */
  label: string;
}

export interface HelpSuggestionGroup {
  pageKey: string;
  questions: string[];
}

export interface HelpNewFeature {
  id: string;
  title: string;
  releasedAt: string;
}

export interface HelpBootstrap {
  /** ONLY the routes this user may open — the server filters by role/permission. */
  routes: HelpRoute[];
  suggestions: HelpSuggestionGroup[];
  defaultSuggestions: string[];
  newFeatures: HelpNewFeature[];
  hasNew: boolean;
}

export interface HelpNavigateAction {
  type: 'navigate';
  label: string;
  routeKey: string;
  path: string;
}

export type HelpAction = HelpNavigateAction;

/** A handbook page (/doc/...) the answer was taken from. */
export interface HelpDocLink {
  id: string;
  title: string;
  path: string;
}

/**
 * Only real handbook paths become links: `/doc/<chapter>/<page>`. The server
 * builds them from the article ids, but the panel does not take that on trust.
 */
export const validateHelpDocLinks = (raw: unknown): HelpDocLink[] =>
  Array.isArray(raw)
    ? raw
        .filter(
          (link: any): link is HelpDocLink =>
            link &&
            typeof link.id === 'string' &&
            typeof link.title === 'string' &&
            typeof link.path === 'string' &&
            /^\/doc\/[a-z0-9-]+\/[a-z0-9-]+$/.test(link.path),
        )
        .slice(0, 3)
    : [];

export type HelpAnsweredBy = 'knowledge' | 'llm' | 'fallback';

export interface HelpChatResponse {
  answer: string;
  actions: HelpAction[];
  relatedQuestions: string[];
  sourceIds: string[];
  /** Handbook pages to read the full guide on; empty when nothing matched. */
  docLinks: HelpDocLink[];
  answeredBy: HelpAnsweredBy;
  messageId: string;
  /**
   * The assistant could not answer — the panel then offers the contact form
   * instead of leaving the user at a dead end. Set both for the canned
   * fallback and for an LLM answer whose context did not cover the question.
   */
  escalate?: boolean;
}

export interface HelpHistoryEntry {
  role: 'user' | 'assistant';
  content: string;
}

export interface HelpChatRequest {
  question: string;
  pageKey?: string;
  currentRoute?: string;
  lang?: string;
  history?: HelpHistoryEntry[];
}

export type HelpEventName =
  | 'help_chat_opened'
  | 'help_question_asked'
  | 'help_article_matched'
  | 'help_navigation_clicked'
  | 'help_doc_opened'
  | 'help_answer_failed'
  | 'help_no_answer'
  | 'thumbs_up'
  | 'thumbs_down';

export interface HelpEventPayload {
  event: HelpEventName;
  routeKey?: string;
  pageKey?: string;
  messageId?: string;
}

/** Hard caps from the contract. */
export const MAX_QUESTION_LENGTH = 500;
export const MAX_REASON_LENGTH = 300;
export const MAX_HISTORY_ENTRIES = 6;
export const MAX_HISTORY_CONTENT = 500;
/** How many messages stay in the visible conversation. */
export const MAX_VISIBLE_MESSAGES = 20;

export const EMPTY_BOOTSTRAP: HelpBootstrap = {
  routes: [],
  suggestions: [],
  defaultSuggestions: [],
  newFeatures: [],
  hasNew: false,
};

export const helpAssistantQueryKeys = {
  bootstrap: (lang: string) => ['help-assistant', 'bootstrap', lang] as const,
};

/** Normalises whatever the server sends into a shape the UI can trust. */
const normaliseBootstrap = (raw: any): HelpBootstrap => ({
  routes: Array.isArray(raw?.routes)
    ? raw.routes.filter(
        (r: any) => r && typeof r.key === 'string' && typeof r.path === 'string',
      )
    : [],
  suggestions: Array.isArray(raw?.suggestions)
    ? raw.suggestions.filter(
        (s: any) => s && typeof s.pageKey === 'string' && Array.isArray(s.questions),
      )
    : [],
  defaultSuggestions: Array.isArray(raw?.defaultSuggestions)
    ? raw.defaultSuggestions.filter((q: any) => typeof q === 'string')
    : [],
  newFeatures: Array.isArray(raw?.newFeatures)
    ? raw.newFeatures.filter((f: any) => f && typeof f.id === 'string')
    : [],
  hasNew: Boolean(raw?.hasNew),
});

export const fetchHelpBootstrap = async (
  lang: string,
  pageKey: string,
): Promise<HelpBootstrap> => {
  const response = await apiClient.get('/help-assistant/bootstrap', {
    params: { lang, pageKey },
  });
  return normaliseBootstrap(response.data?.data ?? response.data);
};

export const postHelpChat = async (payload: HelpChatRequest): Promise<HelpChatResponse> => {
  const response = await apiClient.post('/help-assistant/chat', payload);
  const raw = response.data?.data ?? response.data;
  return {
    answer: typeof raw?.answer === 'string' ? raw.answer : '',
    actions: Array.isArray(raw?.actions) ? raw.actions : [],
    relatedQuestions: Array.isArray(raw?.relatedQuestions) ? raw.relatedQuestions : [],
    sourceIds: Array.isArray(raw?.sourceIds) ? raw.sourceIds : [],
    docLinks: validateHelpDocLinks(raw?.docLinks),
    answeredBy: (raw?.answeredBy as HelpAnsweredBy) ?? 'llm',
    messageId: typeof raw?.messageId === 'string' ? raw.messageId : '',
    escalate: raw?.escalate === true,
  };
};

export const postHelpFeedback = async (payload: {
  messageId: string;
  helpful: boolean;
  reason?: string;
}): Promise<void> => {
  await apiClient.post('/help-assistant/feedback', payload);
};

/**
 * Analytics ping. Deliberately fire-and-forget: a missing or failing endpoint
 * must never surface in the chat or reject an unhandled promise.
 */
export const postHelpEvent = (payload: HelpEventPayload): void => {
  void apiClient.post('/help-assistant/event', payload).catch(() => undefined);
};

/**
 * Builds the `history` field: newest entries last, at most six, each trimmed
 * to 500 characters. Callers pass the visible conversation without the
 * greeting.
 */
export const buildHelpHistory = (
  entries: Array<{ role: 'user' | 'assistant'; content: string }>,
): HelpHistoryEntry[] =>
  entries
    // last 4 turns = 8 entries at most, then the hard cap of 6 from the contract
    .slice(-8)
    .slice(-MAX_HISTORY_ENTRIES)
    .map(entry => ({
      role: entry.role,
      content: entry.content.slice(0, MAX_HISTORY_CONTENT),
    }));
